import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Lock, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/Auth";
import { errMsg } from "@/lib/api";
import { Field } from "@/components/Field";


export default function Account() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mode, setMode] = useState(loc.search.includes("register") ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Signed in" : "Account created — your progress is now saved");
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="card p-6 sm:p-8 fade-up">
        <div className="flex p-1 rounded-full" style={{ background: "var(--muted-bg)" }}>
          {["login", "register"].map((m) => (
            <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }} className="flex-1 h-9 rounded-full text-sm font-medium transition-colors"
              style={mode === m ? { background: "var(--surface)", boxShadow: "0 1px 3px rgba(0,0,0,.12)" } : { color: "var(--ink-3)" }}>
              {m === "login" ? "Sign in" : "Create account"}
            </button>
          ))}
        </div>
        <h1 className="mt-6 font-display text-2xl font-bold">{mode === "login" ? "Welcome back" : "Save your progress"}</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--ink-3)" }}>
          {mode === "login" ? "Review past attempts and your weak spots on any device." : "Free and optional. Attempts you've done on this device as a guest will be added to your account."}
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
          {mode === "register" && <Field label="Name (optional)" testid="auth-name-input" value={form.name} onChange={set("name")} autoComplete="name" />}
          <Field label="Email" testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} autoComplete="email" />
          <Field label="Password" testid="auth-password-input" type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} autoComplete={mode === "login" ? "current-password" : "new-password"} />
          {mode === "register" && <p className="text-xs" style={{ color: "var(--ink-3)" }}>At least 8 characters.</p>}
          {mode === "login" && (
            <Link to="/forgot-password" data-testid="auth-forgot-password-link" className="block text-right text-sm -mt-2" style={{ color: "var(--accent)" }}>Forgot password?</Link>
          )}
          {error && <p data-testid="auth-error" className="text-sm" style={{ color: "var(--alert)" }}>{error}</p>}
          <button data-testid="auth-submit-button" disabled={busy} className="btn-primary w-full h-11 rounded-full font-semibold flex items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        <Link to="/" data-testid="auth-continue-guest-link" className="mt-4 h-11 rounded-full btn-ghost text-sm font-medium flex items-center justify-center gap-2">
          <UserRound size={15} /> Continue as guest
        </Link>
        <p className="mt-6 text-xs flex gap-2" style={{ color: "var(--ink-3)" }}>
          <Lock size={13} className="shrink-0 mt-0.5" />
          Passwords are stored as salted bcrypt hashes and never visible to anyone. Sessions use secure, HTTP-only cookies. We only store your email, name and practice attempts.
        </p>
      </div>
    </div>
  );
}
